import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { UserContext } from "../../Context/UserContext";
import { FaArrowLeft, FaClock, FaSlidersH, FaSearch, FaFilter, FaCheck, FaTimes } from "react-icons/fa";
import "./kitchen.css";

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
          <h3>Something went wrong in the Kitchen view.</h3>
          <p>{this.state.error?.message || "Unknown error"}</p>
          <button
            style={{
              padding: "6px 12px",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Kitchen() {
  const navigate = useNavigate();
  const [savedOrders, setSavedOrders] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedKitchen, setSelectedKitchen] = useState(null);
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllStatusPopup, setShowAllStatusPopup] = useState(false);
  const [pickedUpItems, setPreparedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeDesign, setActiveDesign] = useState("design1"); // design1 = table, design2 = kanban

  const [showColumnManager, setShowColumnManager] = useState(false);

  const defaultColumns = {
    Date: true, Time: true, Customer: true, "Order No": true, "Order Type": true,
    Table: true, "Item & Addons": true, Combos: true, "Combo Offer Sub-Items": true,
    Images: true, Quantity: true, Category: true, Status: true, "Kitchen Note": true, Action: true,
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("kitchenVisibleColumns");
    if (saved) {
      try { return { ...defaultColumns, ...JSON.parse(saved) }; }
      catch (e) { return defaultColumns; }
    }
    return defaultColumns;
  });

  const [tempVisibleColumns, setTempVisibleColumns] = useState(visibleColumns);

  useEffect(() => {
    if (showColumnManager) {
      setTempVisibleColumns(visibleColumns);
    }
  }, [showColumnManager, visibleColumns]);

  const toggleColumn = (col) => {
    setTempVisibleColumns((prev) => ({ ...prev, [col]: !prev[col] }));
  };

  const handleSaveColumns = () => {
    setVisibleColumns(tempVisibleColumns);
    localStorage.setItem("kitchenVisibleColumns", JSON.stringify(tempVisibleColumns));
    setShowColumnManager(false);
  };

  // Keyboard Shortcuts Integration
  useEffect(() => {
    const isKeyboardShortcutsEnabled = localStorage.getItem("isKeyboardShortcutsEnabled") === "true";
    if (!isKeyboardShortcutsEnabled) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Backspace' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        navigate(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);
  const [lastHourSearchDate, setLastHourSearchDate] = useState("");
  const [allStatusSearchDate, setAllStatusSearchDate] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemDetailsCache, setItemDetailsCache] = useState({});
  const { baseUrl, configLoading } = useContext(UserContext);
  const currentYear = new Date().getFullYear().toString();

  // NEW: Normalize order type to shorthands (DIN, TAK, OND)
  const normalizeOrderType = (orderType) => {
    if (!orderType) return "N/A";
    const normalized = orderType.trim().toLowerCase();
    const orderTypeMap = {
      "dine in": "DIN",
      "dine-in": "DIN",
      takeaway: "TAK",
      "take away": "TAK",
      "online delivery": "OND",
      delivery: "OND",
    };
    return orderTypeMap[normalized] || orderType;
  };

  // NEW: Standardize Token No prefixes for display (e.g., Take Away-0077 -> TAK-0077)
  const formatTokenNo = (tokenNo) => {
    if (!tokenNo) return "N/A";
    return tokenNo
      .replace(/Takeaway-/i, "TAK-")
      .replace(/Take Away-/i, "TAK-")
      .replace(/Dine In-/i, "DIN-")
      .replace(/Dine-In-/i, "DIN-")
      .replace(/Online Delivery-/i, "OND-")
      .replace(/Delivery-/i, "OND-");
  };

  // NEW: Helper to safely match kitchen (handles array or string)
  const isKitchenMatch = (kitchen, selectedKitchen) => {
    if (!kitchen || !selectedKitchen) return false;
    const sKitchen = String(selectedKitchen).trim().toLowerCase();
    if (Array.isArray(kitchen)) {
      return kitchen.some(k => String(k).trim().toLowerCase() === sKitchen);
    }
    return String(kitchen).trim().toLowerCase() === sKitchen;
  };

  // Helper to get fetched kitchen
  const getFetchedKitchen = (type, mainItemName, subName, fallback = null) => {
    const details = itemDetailsCache[mainItemName];
    if (!details || !subName) {
      console.log(`No details for ${mainItemName} or missing subName: ${subName}`);
      return fallback;
    }
    const list = type === "addon" ? details.addons : details.combos;
    const sub = list.find((s) => s.name1 === subName);
    const kitchen = sub ? sub.kitchen : fallback;
    console.log(`Fetched kitchen for ${type} ${mainItemName}/${subName}: ${kitchen}`);
    return kitchen;
  };

  // Retry logic with exponential backoff
  const retryRequest = async (fn, retries = 3, delay = 1000) => {
    for (let i = 0; i < retries; i++) {
      try {
        return await fn();
      } catch (error) {
        console.error(`API call failed, retrying (${i + 1}/${retries})...`, error);
        if (i === retries - 1) throw error;
        await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, i)));
      }
    }
  };

  // Fetch active orders - Refactored to useCallback for Socket.io
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${baseUrl}/api/activeorders`;
      const response = await axios.get(url, { timeout: 8000 });
      if (Array.isArray(response.data)) {
        const ordersWithStatuses = response.data.map((order) => ({
          ...order,
          cartItems: Array.isArray(order.cartItems)
            ? order.cartItems.map((item) => ({
              ...item,
              name: item.is_combo_offer ? (item.offer_description || item.name || item.item_name || "Combo Offer") : (item.name || item.item_name || "Unknown Item"),
              item_name: item.is_combo_offer ? (item.offer_description || item.name || item.item_name || "Combo Offer") : (item.name || item.item_name || "Unknown Item"),
              kitchenStatuses: item.kitchenStatuses || {},
              isCombo: item.isCombo || item.is_combo_offer || false,
              comboItems: item.comboItems || [],
            }))
            : [],
        }));
        setSavedOrders(ordersWithStatuses);
        setErrorMessage("");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setErrorMessage("Failed to fetch orders.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  // Fetch historical picked-up items
  const fetchPreparedItems = useCallback(async () => {
    try {
      const response = await retryRequest(() =>
        axios.get(`${baseUrl}/api/picked-up-items`, { timeout: 8000 })
      );
      if (response.data.success && Array.isArray(response.data.pickedUpItems)) {
        setPreparedItems(response.data.pickedUpItems);
      }
    } catch (error) {
      console.error("Error fetching picked-up items:", error);
    }
  }, [baseUrl]);

  // Fetch System Settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await retryRequest(() =>
        axios.get(`${baseUrl}/api/settings`, { timeout: 8000 })
      );
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, [baseUrl]);

  // Initial combined fetch
  useEffect(() => {
    if (configLoading) return;
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchOrders(),
          fetchPreparedItems(),
          fetchSettings()
        ]);
      } catch (err) {
        console.error("Error loading kitchen data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [baseUrl, configLoading, fetchOrders, fetchPreparedItems, fetchSettings]);

  // Socket.io Integration
  useEffect(() => {
    let socketUrl = baseUrl;
    if (!socketUrl) {
      if (window.location.hostname !== 'localhost' && window.location.port !== '6034') {
        socketUrl = `${window.location.protocol}//${window.location.hostname}:6034`;
      } else {
        socketUrl = window.location.origin;
      }
    }
    const socket = io(socketUrl, {
      transports: ['polling', 'websocket'],
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });
    socket.on("connect", () => console.log("Connected to socket server"));
    socket.on("order_update", () => fetchOrders());
    return () => socket.disconnect();
  }, [baseUrl, fetchOrders]);

  // Fetch item details
  useEffect(() => {
    const fetchItemDetails = async () => {
      const itemsToFetch = savedOrders
        .filter((order) => Array.isArray(order.cartItems))
        .flatMap((order) => order.cartItems)
        .filter((item) => !itemDetailsCache[item.name]);
      for (const item of itemsToFetch) {
        try {
          const itemName = encodeURIComponent(item.name);
          const response = await retryRequest(() =>
            axios.get(`${baseUrl}/api/items/${itemName}`, { timeout: 8000 })
          );
          console.log(`Fetched details for item ${item.name}:`, response.data);
          if (response.data) {
            const fetchedData = {
              image: response.data.image || item.image || "/static/images/default-item.jpg",
              addons: Array.isArray(response.data.addons)
                ? response.data.addons.map((addon) => ({
                  name1: addon.name1 || "Unknown",
                  addon_image: addon.addon_image || "/static/images/default-item.jpg",
                  kitchen: addon.kitchen || "Unknown",
                }))
                : [],
              combos: Array.isArray(response.data.combos)
                ? response.data.combos.map((combo) => ({
                  name1: combo.name1 || "Unknown",
                  combo_image: combo.combo_image || "/static/images/default-item.jpg",
                  kitchen: combo.kitchen || "Unknown",
                  size: combo.size || "M",
                  spicy: combo.spicy || false,
                }))
                : [],
            };
            setItemDetailsCache((prev) => ({
              ...prev,
              [item.name]: fetchedData,
            }));
          }
        } catch (error) {
          console.error(`Error fetching details for item ${item.name}:`, error);
          setItemDetailsCache((prev) => ({
            ...prev,
            [item.name]: {
              image: item.image || "/static/images/default-item.jpg",
              addons: [],
              combos: [],
            },
          }));
        }
      }
    };
    if (savedOrders.length > 0) {
      fetchItemDetails();
    }
  }, [savedOrders, baseUrl]);

  const isOrderFullyServed = (order) => {
    const allItemsServed = order.cartItems.every((i) => (i.servedQuantity || 0) >= (i.quantity || 1));
    const allSubItemsServed = order.cartItems.every((i) => {
      const addonsServed = !i.addonQuantities || Object.keys(i.addonQuantities).length === 0 ||
        Object.keys(i.addonQuantities).every(a => i.servedAddons?.[a]);
      const combosServed = !i.comboQuantities || Object.keys(i.comboQuantities).length === 0 ||
        Object.keys(i.comboQuantities).every(c => i.servedCombos?.[c]);
      const comboOfferItemsServed = !i.is_combo_offer || !i.comboItems ||
        i.comboItems.every(ci => i.servedComboItems?.[ci.name]);
      return addonsServed && combosServed && comboOfferItemsServed;
    });
    return allItemsServed && allSubItemsServed;
  };

  const isOrderCompleted = (order) => {
    if (!order) return false;
    const orderFlowType = settings?.order_flow_type || "KDS_AUTO_SERVE";
    const bypassKitchen = orderFlowType === 'DIRECT_SERVE' || orderFlowType === 'SEAT_ORDER_SERVE';
    if (bypassKitchen) {
      return order.paid;
    }
    return order.paid && isOrderFullyServed(order);
  };

  // Derive kitchens (including from combo offer sub-items)
  let derivedKitchens = [
    ...new Set(
      savedOrders
        .filter((order) => Array.isArray(order.cartItems) && !isOrderCompleted(order))
        .flatMap((order) =>
          order.cartItems.reduce((acc, item) => {
            const statuses = item.kitchenStatuses || {};
            // Main item kitchen
            if (item.kitchen) {
              acc.push(item.kitchen);
            }
            // Addons
            if (item.addonQuantities) {
              Object.entries(item.addonQuantities).forEach(([addonName, qty]) => {
                if (qty > 0) {
                  const k = getFetchedKitchen("addon", item.name, addonName, item.addonVariants?.[addonName]?.kitchen);
                  if (k) {
                    acc.push(k);
                  }
                }
              });
            }
            // Combos
            if (item.comboQuantities) {
              Object.entries(item.comboQuantities).forEach(([comboName, qty]) => {
                if (qty > 0) {
                  const k = getFetchedKitchen("combo", item.name, comboName, item.comboVariants?.[comboName]?.kitchen);
                  if (k) {
                    acc.push(k);
                  }
                }
              });
            }
            // FIXED: For combo offers, add kitchens from sub-items (comboItems)
            if (item.is_combo_offer && Array.isArray(item.comboItems)) {
              item.comboItems.forEach((subItem) => {
                if (subItem.kitchen) {
                  acc.push(subItem.kitchen);
                }
              });
            }
            return acc;
          }, [])
        )
        .flat()
        .filter((kitchen) => kitchen && typeof kitchen === "string")
    ),
  ];

  const kitchens = derivedKitchens.length > 0 ? derivedKitchens : ["Main Kitchen"];

  useEffect(() => {
    console.log("Derived kitchens from savedOrders:", savedOrders, kitchens);
    if (kitchens.length > 0 && (!selectedKitchen || !kitchens.includes(selectedKitchen))) {
      setSelectedKitchen(kitchens[0]);
    } else if (kitchens.length === 0 && selectedKitchen) {
      console.log("No kitchens available, retaining selectedKitchen:", selectedKitchen);
    }
  }, [kitchens, selectedKitchen]);


  // Filter orders for selected kitchen (including combo offer sub-items)
  const filteredOrders = savedOrders
    .filter((order) => !isOrderCompleted(order))
    .map((order) => {
      const relevantItems = Array.isArray(order.cartItems)
        ? order.cartItems
          .map((item) => {
            let filteredAddons = {};
            let filteredAddonVariants = {};
            let filteredAddonCustomVariantsDetails = {};
            if (item.addonQuantities && item.addonVariants) {
              Object.entries(item.addonQuantities).forEach(([addonName, qty]) => {
                if (qty > 0) {
                  const addonKitchen = getFetchedKitchen(
                    "addon",
                    item.name,
                    addonName,
                    item.addonVariants[addonName]?.kitchen
                  );
                  if (isKitchenMatch(addonKitchen, selectedKitchen)) {
                    filteredAddons[addonName] = qty;
                    filteredAddonVariants[addonName] = {
                      ...item.addonVariants[addonName],
                      kitchen: addonKitchen,
                    };
                    filteredAddonCustomVariantsDetails[addonName] =
                      item.addonCustomVariantsDetails?.[addonName] || {};
                  }
                }
              });
            }
            let filteredCombos = {};
            let filteredComboVariants = {};
            let filteredComboCustomVariantsDetails = {};
            if (item.comboQuantities && item.comboVariants) {
              Object.entries(item.comboQuantities).forEach(([comboName, qty]) => {
                if (qty > 0) {
                  const comboKitchen = getFetchedKitchen(
                    "combo",
                    item.name,
                    comboName,
                    item.comboVariants[comboName]?.kitchen
                  );
                  if (isKitchenMatch(comboKitchen, selectedKitchen)) {
                    filteredCombos[comboName] = qty;
                    filteredComboVariants[comboName] = {
                      ...item.comboVariants[comboName],
                      kitchen: comboKitchen,
                    };
                    filteredComboCustomVariantsDetails[comboName] =
                      item.comboCustomVariantsDetails?.[comboName] || {};
                  }
                }
              });
            }
            // FIXED: For combo offers, filter sub-items (comboItems) based on their kitchen
            let filteredComboOfferSubItems = [];
            if (item.is_combo_offer && Array.isArray(item.comboItems)) {
              filteredComboOfferSubItems = item.comboItems.filter((subItem) => isKitchenMatch(subItem.kitchen, selectedKitchen));
            }
            // Filter Toppings
            let filteredItemToppings = {};
            if (item.itemToppings) {
              Object.entries(item.itemToppings).forEach(([id, t]) => {
                const toppingKitchen = t.kitchen || item.kitchen || "Main Kitchen";
                if (isKitchenMatch(toppingKitchen, selectedKitchen)) {
                  filteredItemToppings[id] = t;
                }
              });
            }

            let filteredAddonToppings = {};
            if (item.addonToppings) {
              Object.entries(item.addonToppings).forEach(([addonName, toppings]) => {
                let currentAddonToppings = {};
                Object.entries(toppings).forEach(([id, t]) => {
                  const toppingKitchen = t.kitchen || item.kitchen || "Main Kitchen";
                  if (isKitchenMatch(toppingKitchen, selectedKitchen)) {
                    currentAddonToppings[id] = t;
                  }
                });
                if (Object.keys(currentAddonToppings).length > 0) {
                  filteredAddonToppings[addonName] = currentAddonToppings;
                }
              });
            }

            let filteredComboToppings = {};
            if (item.comboToppings) {
              Object.entries(item.comboToppings).forEach(([comboName, toppings]) => {
                let currentComboToppings = {};
                Object.entries(toppings).forEach(([id, t]) => {
                  const toppingKitchen = t.kitchen || item.kitchen || "Main Kitchen";
                  if (isKitchenMatch(toppingKitchen, selectedKitchen)) {
                    currentComboToppings[id] = t;
                  }
                });
                if (Object.keys(currentComboToppings).length > 0) {
                  filteredComboToppings[comboName] = currentComboToppings;
                }
              });
            }

            return {
              ...item,
              addonQuantities: filteredAddons,
              addonVariants: filteredAddonVariants,
              addonCustomVariantsDetails: filteredAddonCustomVariantsDetails,
              comboQuantities: filteredCombos,
              comboVariants: filteredComboVariants,
              comboCustomVariantsDetails: filteredComboCustomVariantsDetails,
              filteredComboOfferSubItems,
              itemToppings: filteredItemToppings,
              addonToppings: filteredAddonToppings,
              comboToppings: filteredComboToppings,
              displayInKitchen:
                isKitchenMatch(item.kitchen, selectedKitchen) ||
                Object.keys(filteredAddons).length > 0 ||
                Object.keys(filteredCombos).length > 0 ||
                filteredComboOfferSubItems.length > 0 ||
                Object.keys(filteredItemToppings).length > 0 ||
                Object.keys(filteredAddonToppings).length > 0 ||
                Object.keys(filteredComboToppings).length > 0,
              kitchenStatuses: item.kitchenStatuses || {},
            };
          })
          .filter((item) => item.displayInKitchen)
        : [];
      return { ...order, cartItems: relevantItems };
    })
    .filter((order) => order.cartItems.length > 0);

  // Formatting helpers
  const formatItemVariants = (item) => {
    const variants = [];
    if (item.selectedSize) variants.push(`Size: ${item.selectedSize}`);
    if (item.icePreference === "with_ice") variants.push(`Ice: With Ice`);
    if (item.isSpicy === true) variants.push(`Spicy: Yes`);
    if (item.sugarLevel && item.sugarLevel !== "medium") {
      variants.push(
        `Sugar: ${item.sugarLevel.charAt(0).toUpperCase() + item.sugarLevel.slice(1)}`
      );
    }
    return variants.length > 0 ? `(${variants.join(", ")})` : "";
  };

  const formatCustomVariants = (customVariantsDetails) => {
    if (!customVariantsDetails) return "";
    const custom = Object.values(customVariantsDetails)
      .map((v) => `${v.heading}: ${v.name}`)
      .join(", ");
    return custom ? `Custom: ${custom}` : "";
  };

  const formatAddonVariants = (addonVariants) => {
    const variants = [];
    if (addonVariants?.size) {
      variants.push(`Size: ${addonVariants.size}`);
    }
    if (addonVariants?.spicy === true) {
      variants.push(`Spicy: Yes`);
    }
    if (addonVariants?.sugar && addonVariants.sugar !== "medium") {
      variants.push(
        `Sugar: ${addonVariants.sugar.charAt(0).toUpperCase() + addonVariants.sugar.slice(1)}`
      );
    }
    return variants.length > 0 ? `(${variants.join(", ")})` : "";
  };

  const formatAddonCustomVariants = (addonCustomVariantsDetails) => {
    if (!addonCustomVariantsDetails) return "";
    const custom = Object.values(addonCustomVariantsDetails)
      .map((v) => `${v.heading}: ${v.name}`)
      .join(", ");
    return custom ? `Custom: ${custom}` : "";
  };

  const formatComboVariants = (comboVariants) => {
    const variants = [];
    if (comboVariants?.size) {
      variants.push(`Size: ${comboVariants.size}`);
    }
    if (comboVariants?.spicy === true) {
      variants.push(`Spicy: Yes`);
    }
    if (comboVariants?.sugar && comboVariants.sugar !== "medium") {
      variants.push(
        `Sugar: ${comboVariants.sugar.charAt(0).toUpperCase() + comboVariants.sugar.slice(1)}`
      );
    }
    return variants.length > 0 ? `(${variants.join(", ")})` : "";
  };

  const formatComboCustomVariants = (comboCustomVariantsDetails) => {
    if (!comboCustomVariantsDetails) return "";
    const custom = Object.values(comboCustomVariantsDetails)
      .map((v) => `${v.heading}: ${v.name}`)
      .join(", ");
    return custom ? `Custom: ${custom}` : "";
  };

  // NEW: Format notes for display
  const formatNotes = (notes, key) => {
    return notes?.[key] ? notes[key].trim() : '';
  };

  // FIXED: Updated helper to render combo offer sub-items in kitchen table (pass parent 'item' to access kitchenNotes)
  const renderComboOfferSubItems = (filteredComboOfferSubItems, itemQuantity, item) => {
    if (!filteredComboOfferSubItems || filteredComboOfferSubItems.length === 0) return null;
    return filteredComboOfferSubItems.map((subItem, idx) => (
      <div
        key={`sub-${idx}`}
        style={{ fontSize: "12px", color: "#555", marginLeft: "10px", padding: "4px", borderLeft: "2px solid #007bff" }}
      >
        - Sub-Item: {subItem.name} <span style={{ fontSize: "10px", color: "#888", fontStyle: "italic" }}>({subItem.category || "Combo Sub-Item"})</span> x{itemQuantity} (Kitchen: {subItem.kitchen})
        <div>Status: {subItem.status || "Pending"}</div>
        {/* NEW: Kitchen Note for Sub-Item if available */}
        {formatNotes(item.kitchenNotes, subItem.name) && (
          <div style={{ fontSize: "11px", color: "#007bff", marginTop: "2px" }}>
            Note: {formatNotes(item.kitchenNotes, subItem.name)}
          </div>
        )}
      </div>
    ));
  };

  // Popup data logic
  const getLastHourItems = () => {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    // Filter by last hour first
    let filteredItems = (pickedUpItems || []).filter((entry) => {
      const pickupTime = new Date(entry.pickupTime);
      return pickupTime >= oneHourAgo;
    });

    if (lastHourSearchDate) {
      // STRICT FILTER: Show ONLY items matching the date
      filteredItems = filteredItems.filter((entry) => {
        const itemDate = new Date(entry.pickupTime);
        const itemDateStr = itemDate.getFullYear() + '-' + String(itemDate.getMonth() + 1).padStart(2, '0') + '-' + String(itemDate.getDate()).padStart(2, '0');
        return itemDateStr === lastHourSearchDate;
      });
    }

    // Deduplicate by order
    const uniqueOrders = new Map();
    filteredItems.forEach((entry) => {
      const key = entry.orderId || entry.orderNo || entry.tokenNo || entry._id;
      if (!uniqueOrders.has(key)) {
        uniqueOrders.set(key, entry);
      } else {
        if (new Date(entry.pickupTime) > new Date(uniqueOrders.get(key).pickupTime)) {
          uniqueOrders.set(key, entry);
        }
      }
    });

    return Array.from(uniqueOrders.values()).sort((a, b) => new Date(b.pickupTime) - new Date(a.pickupTime));
  };

  const getAllPreparedItems = () => {
    let filteredItems = (pickedUpItems || []).slice();

    if (allStatusSearchDate) {
      // STRICT FILTER: Show ONLY items matching the date
      filteredItems = filteredItems.filter((entry) => {
        const itemDate = new Date(entry.pickupTime);
        const itemDateStr = itemDate.getFullYear() + '-' + String(itemDate.getMonth() + 1).padStart(2, '0') + '-' + String(itemDate.getDate()).padStart(2, '0');
        return itemDateStr === allStatusSearchDate;
      });
    }

    // Deduplicate by order
    const uniqueOrders = new Map();
    filteredItems.forEach((entry) => {
      const key = entry.orderId || entry.orderNo || entry.tokenNo || entry._id;
      if (!uniqueOrders.has(key)) {
        uniqueOrders.set(key, entry);
      } else {
        if (new Date(entry.pickupTime) > new Date(uniqueOrders.get(key).pickupTime)) {
          uniqueOrders.set(key, entry);
        }
      }
    });

    return Array.from(uniqueOrders.values()).sort((a, b) => new Date(b.pickupTime) - new Date(a.pickupTime));
  };

  // FIXED: Updated to handle combo offer sub-items in flattened list
  const getFlattenedSubItems = (entry) => {
    const allItems = Array.isArray(entry.items) ? entry.items : [entry];
    const subItems = [];
    allItems.forEach((item) => {
      subItems.push({
        id: `main-${item.id || Math.random()}`,
        type: "main",
        name: item.item_name || "Unknown Item",
        quantity: item.quantity || 1,
        category: item.category || "N/A",
        kitchen: item.kitchen || "N/A",
      });
      if (item.addons && Array.isArray(item.addons)) {
        item.addons.forEach((addon) => {
          if (addon.addon_quantity > 0) {
            subItems.push({
              id: `addon-${addon.name1 || Math.random()}`,
              type: "addon",
              name: `+ Addon: ${addon.name1}`,
              quantity: addon.addon_quantity,
              // NEW: Use populated category for addon
              category: item.addonCategories?.[addon.name1] || "Addon",
              kitchen: addon.kitchen || "N/A",
            });
          }
        });
      }
      if (item.selectedCombos && Array.isArray(item.selectedCombos)) {
        item.selectedCombos.forEach((combo) => {
          const comboQty = combo.combo_quantity || 1;
          if (comboQty > 0) {
            const spicy = combo.isSpicy ? " (Spicy)" : "";
            subItems.push({
              id: `combo-${combo.name1 || Math.random()}`,
              type: "combo",
              name: `+ Combo: ${combo.name1} (${combo.size || "M"})${spicy}`,
              quantity: comboQty,
              // NEW: Use populated category for combo
              category: item.comboCategories?.[combo.name1] || "Combo",
              kitchen: combo.kitchen || "N/A",
            });
          }
        });
      }
      // FIXED: Add sub-items from combo offers
      if (item.is_combo_offer && Array.isArray(item.comboItems)) {
        item.comboItems.forEach((subItem) => {
          subItems.push({
            id: `combooffer-sub-${subItem.name || Math.random()}`,
            type: "combooffer-sub",
            name: `- Combo Sub: ${subItem.name}`,
            quantity: item.quantity || 1,
            // NEW: Use populated category for sub-item
            category: subItem.category || "Combo Sub-Item",
            kitchen: subItem.kitchen || "N/A",
          });
        });
      }
      if (item.itemToppings && typeof item.itemToppings === 'object') {
        Object.values(item.itemToppings).forEach((t) => {
          subItems.push({
            id: `topping-${t.name || Math.random()}`,
            type: "itemTopping",
            name: `+ ${t.name} (Topping)`,
            quantity: t.qty || 1,
            category: "Topping",
            kitchen: t.kitchen || "N/A",
          });
        });
      }
      if (item.addonToppings && typeof item.addonToppings === 'object') {
        Object.entries(item.addonToppings).forEach(([addonName, toppings]) => {
          Object.values(toppings).forEach((t) => {
            subItems.push({
              id: `addon-topping-${t.name || Math.random()}`,
              type: "addonTopping",
              name: `+ ${t.name} (Topping for ${addonName})`,
              quantity: t.qty || 1,
              category: "Topping",
              kitchen: t.kitchen || "N/A",
            });
          });
        });
      }
      if (item.comboToppings && typeof item.comboToppings === 'object') {
        Object.entries(item.comboToppings).forEach(([comboName, toppings]) => {
          Object.values(toppings).forEach((t) => {
            subItems.push({
              id: `combo-topping-${t.name || Math.random()}`,
              type: "comboTopping",
              name: `+ ${t.name} (Topping for ${comboName})`,
              quantity: t.qty || 1,
              category: "Topping",
              kitchen: t.kitchen || "N/A",
            });
          });
        });
      }
    });
    return subItems;
  };

  // Action handlers
  const handleMarkPrepared = async (orderId, itemId, kitchen) => {
    try {
      const response = await retryRequest(() =>
        axios.post(
          `${baseUrl}/api/activeorders/${orderId}/items/${itemId}/mark-prepared`,
          { kitchen },
          { headers: { "Content-Type": "application/json" }, timeout: 8000 }
        )
      );
      if (response.data.success) {
        setSavedOrders((prev) =>
          prev.map((order) =>
            order.orderId === orderId
              ? {
                ...order,
                cartItems: order.cartItems.map((item) =>
                  String(item.id) === String(itemId)
                    ? {
                      ...item,
                      kitchenStatuses: {
                        ...item.kitchenStatuses,
                        [kitchen]: response.data.status,
                      },
                    }
                    : item
                ),
              }
              : order
          )
        );
      } else {
        console.error("Failed to mark as prepared:", response.data);
      }
    } catch (error) {
      console.error("Error marking as prepared:", error);
    }
  };

  const handlePickUp = async (orderId, itemId) => {
    try {
      setLoading(true);
      const order = savedOrders.find((o) => o.orderId === orderId);
      const pickedItem = order?.cartItems.find((item) => String(item.id) === String(itemId));
      if (pickedItem && (pickedItem.kitchenStatuses?.[selectedKitchen] === "Prepared" || getEffectiveKitchenStatus(pickedItem, selectedKitchen) === "Prepared")) {
        const response = await retryRequest(() =>
          axios.post(
            `${baseUrl}/api/activeorders/${orderId}/items/${itemId}/mark-pickedup`,
            { kitchen: selectedKitchen },
            { headers: { "Content-Type": "application/json" }, timeout: 8000 }
          )
        );
        if (response.data.success) {
          setSavedOrders((prev) =>
            prev.map((order) =>
              order.orderId === orderId
                ? {
                  ...order,
                  cartItems: order.cartItems.map((item) =>
                    String(item.id) === String(itemId)
                      ? {
                        ...item,
                        kitchenStatuses: {
                          ...item.kitchenStatuses,
                          [selectedKitchen]: "PickedUp",
                        },
                      }
                      : item
                  ),
                }
                : order
            )
          );
          await fetchPreparedItems();
          console.log("Item marked as Prepared, status preserved.");
        } else {
          console.error("Failed to mark as picked up:", response.data);
        }
      } else {
        console.error("Item not in Prepared status");
      }
    } catch (error) {
      console.error("Error marking as picked up:", error);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Updated bulk pickup to handle combo offer sub-items if they have separate IDs/statuses
  const handleBulkPickUp = async () => {
    try {
      setLoading(true);
      for (const orderId of selectedCustomers) {
        const order = savedOrders.find((o) => o.orderId === orderId);
        if (!order) continue;
        const itemsToPickUp = order.cartItems.filter(
          (item) =>
            isKitchenMatch(item.kitchen, selectedKitchen) ||
            Object.values(item.addonVariants || {}).some(
              (addon) => isKitchenMatch(addon.kitchen, selectedKitchen) && item.addonQuantities?.[addon.name1]
            ) ||
            Object.values(item.comboVariants || {}).some(
              (combo) => isKitchenMatch(combo.kitchen, selectedKitchen) && item.comboQuantities?.[combo.name1]
            ) ||
            // FIXED: Include combo offer sub-items
            (item.is_combo_offer && item.filteredComboOfferSubItems && item.filteredComboOfferSubItems.length > 0)
        );
        for (const item of itemsToPickUp) {
          if (getEffectiveKitchenStatus(item, selectedKitchen) === "Prepared") {
            await handlePickUp(orderId, item.id);
          } else {
            console.log(`Skipping item ${item.id} in order ${orderId} because status is ${getEffectiveKitchenStatus(item, selectedKitchen)}`);
          }
          // FIXED: If combo offer, handle sub-items (assuming they share the main item's status for simplicity; extend if separate)
          if (item.is_combo_offer && item.filteredComboOfferSubItems) {
            // For now, assume sub-items use main status; if separate, add logic here
          }
        }
      }
      setSelectedCustomers([]);
    } catch (error) {
      console.error("Error during bulk pickup:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkPrepare = async () => {
    try {
      setLoading(true);
      for (const orderId of selectedCustomers) {
        const order = savedOrders.find((o) => o.orderId === orderId);
        if (!order) continue;
        const itemsToPrepare = order.cartItems.filter(
          (item) =>
            isKitchenMatch(item.kitchen, selectedKitchen) ||
            Object.values(item.addonVariants || {}).some(
              (addon) => isKitchenMatch(addon.kitchen, selectedKitchen) && item.addonQuantities?.[addon.name1]
            ) ||
            Object.values(item.comboVariants || {}).some(
              (combo) => isKitchenMatch(combo.kitchen, selectedKitchen) && item.comboQuantities?.[combo.name1]
            ) ||
            (item.is_combo_offer && item.filteredComboOfferSubItems && item.filteredComboOfferSubItems.length > 0)
        );
        for (const item of itemsToPrepare) {
          if (getEffectiveKitchenStatus(item, selectedKitchen) === "Pending") {
            await handleMarkPrepared(orderId, item.id, selectedKitchen);
          }
        }
      }
      setSelectedCustomers([]);
    } catch (error) {
      console.error("Error in bulk prepare:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerCheckboxChange = (orderId) => {
    setSelectedCustomers((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  // NEW: Manual refresh handler for on-demand fetch
  const handleRefresh = () => {
    fetchOrders(); // Reuse the existing fetch function
  };

  const getEffectiveKitchenStatus = (item, kitchenName) => {
    const orderFlowType = settings?.order_flow_type || "KDS_AUTO_SERVE";
    const bypassKitchen = orderFlowType === "DIRECT_SERVE" || orderFlowType === "SEAT_ORDER_SERVE";

    if (bypassKitchen) {
      return "PickedUp";
    }

    const isServed = item.served || (item.servedQuantity && item.servedQuantity >= (Number(item.quantity) || 1));
    if (isServed) {
      return "PickedUp";
    }
    
    const resolvedKitchen = kitchenName || item?.kitchen || (item?.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen";
    return item.kitchenStatuses?.[resolvedKitchen] || "Pending";
  };

  // Style helpers
  const getRowStyle = (status) => {
    switch (status || "Pending") {
      case "Pending":
        return { backgroundColor: "#ffcccc", color: "#333" };
      case "Preparing":
        return { backgroundColor: "#fff3cd", color: "#333" };
      case "Prepared":
        return { backgroundColor: "#dbeafe", color: "#1e3a8a", fontWeight: "500" }; // Blue color as requested
      case "PickedUp":
        return { backgroundColor: "#d4edda", color: "#333" }; // Green for PickedUp
      default:
        return {};
    }
  };

  const getHighlightStyle = (pickupTime, searchDate) => {
    if (searchDate) {
      if (pickupTime?.startsWith(searchDate)) {
        return { backgroundColor: "#87CEEB" };
      }
    }
    return {};
  };

  const getCorrectImageUrl = (imagePath) => {
    if (!imagePath || imagePath.includes("placeholder.png")) return "/static/images/default-item.jpg";
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/")) return baseUrl + imagePath;
    return `${baseUrl}/api/images/${imagePath}`;
  };

  // FIXED: Updated to include images for combo offer sub-items
  const getAddonComboImages = (item) => {
    const images = [];
    const itemDetails = itemDetailsCache[item.name] || {
      image: item.image || "/static/images/default-item.jpg",
      addons: [],
      combos: [],
    };
    if (isKitchenMatch(item.kitchen, selectedKitchen)) {
      images.push({
        src: getCorrectImageUrl(item.image || itemDetails.image),
        label: item.name || "Item",
        type: "item",
        status: item.kitchenStatuses?.[selectedKitchen] || "Pending",
      });
    }
    Object.entries(item.addonQuantities || {})
      .filter(([addonName, qty]) => qty > 0)
      .forEach(([addonName]) => {
        const addon = itemDetails.addons.find((a) => a.name1 === addonName) || {};
        const addonImage =
          item.addonImages?.[addonName] ||
          addon.addon_image || "/static/images/default-item.jpg";
        images.push({
          src: getCorrectImageUrl(addonImage),
          label: addonName || "Addon",
          type: "addon",
          status: item.kitchenStatuses?.[selectedKitchen] || "Pending",
        });
      });
    Object.entries(item.comboQuantities || {})
      .filter(([comboName, qty]) => qty > 0)
      .forEach(([comboName]) => {
        const combo = itemDetails.combos.find((c) => c.name1 === comboName) || {};
        const comboImage =
          item.comboImages?.[comboName] ||
          combo.combo_image || "/static/images/default-item.jpg";
        images.push({
          src: getCorrectImageUrl(comboImage),
          label: comboName || "Combo",
          type: "combo",
          status: item.kitchenStatuses?.[selectedKitchen] || "Pending",
        });
      });
    // FIXED: Add images for combo offer sub-items
    if (item.is_combo_offer && Array.isArray(item.filteredComboOfferSubItems)) {
      item.filteredComboOfferSubItems.forEach((subItem) => {
        images.push({
          src: getCorrectImageUrl(subItem.image || "/static/images/default-item.jpg"),
          label: subItem.name || "Sub-Item",
          type: "combooffer-sub",
          status: item.kitchenStatuses?.[subItem.kitchen] || "Pending", // Use sub-kitchen if available
        });
      });
    }
    return images;
  };

  
  // --- METRICS CALCULATION ---
  const metrics = {
    total: 0,
    pending: 0,
    preparing: 0,
    prepared: 0,
    pickedUp: 0
  };

  const flattenedItems = [];
  filteredOrders.forEach(order => {
    if (order.cartItems) {
      order.cartItems.forEach(item => {
        const status = getEffectiveKitchenStatus(item, selectedKitchen) || "Pending";
        metrics.total++;
        if (status === "Pending") metrics.pending++;
        else if (status === "Preparing") metrics.preparing++;
        else if (status === "Prepared") metrics.prepared++;
        else if (status === "PickedUp" || status === "Picked Up") metrics.pickedUp++;

        flattenedItems.push({
           order,
           item,
           status
        });
      });
    }
  });

  
  const searchedItems = flattenedItems.filter(row => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    const itemName = (row.item.name || row.item.item_name || "").toLowerCase();
    const orderId = (row.order.orderId || "").toLowerCase();
    const customer = (row.order.customerName || row.order.customer || "").toLowerCase();

    // Helper for fuzzy subsequence matching
    const isSubsequence = (text, query) => {
      let i = 0, j = 0;
      while (i < text.length && j < query.length) {
        if (text[i] === query[j]) j++;
        i++;
      }
      return j === query.length;
    };

    return (
      itemName.includes(q) || 
      orderId.includes(q) || 
      customer.includes(q) || 
      isSubsequence(itemName, q)
    );
  });

  const getStatusClass = (status) => {
    if (status === "Pending") return "pending";
    if (status === "Preparing") return "preparing";
    if (status === "Prepared") return "prepared";
    if (status === "PickedUp" || status === "Picked Up") return "pickedup";
    return "";
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(searchedItems.map(row => `${row.order.orderId}|${row.item.id}`));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleBulkMarkPrepared = async () => {
    console.log("handleBulkMarkPrepared called with selectedItems:", selectedItems);
    if (selectedItems.length === 0) return;
    setLoading(true);
    for (const idStr of selectedItems) {
      const [orderId, itemId] = idStr.split("|");
      const order = savedOrders.find(o => o.orderId === orderId);
      const item = order?.cartItems?.find(i => String(i.id) === String(itemId));
      const kitchenToUse = selectedKitchen || item?.kitchen || (item?.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen";
      await handleMarkPrepared(orderId, itemId, kitchenToUse);
    }
    setSelectedItems([]);
    setLoading(false);
  };

  const handleBulkMarkPickedUp = async () => {
    console.log("handleBulkMarkPickedUp called with selectedItems:", selectedItems);
    if (selectedItems.length === 0) return;
    for (const idStr of selectedItems) {
      const [orderId, itemId] = idStr.split("|");
      console.log("Calling handlePickUp for orderId:", orderId, "itemId:", itemId);
      await handlePickUp(orderId, itemId);
    }
    setSelectedItems([]);
  };

  const renderKanbanBoard = () => {
    const tabs = [
      { id: "Pending", label: "Prepare", className: "pending" },
      { id: "Prepared", label: "Prepared", className: "prepared" },
      { id: "PickedUp", label: "Pickedup", className: "pickedup" }
    ];

    return (
      <div className="kitchen-kanban-board">
        {tabs.map(col => {
          const colItems = searchedItems.filter(row => {
             const st = row.status === "Picked Up" ? "PickedUp" : row.status;
             return col.id === "All" || st === col.id;
          });
          return (
            <div key={col.id} className="kanban-column">
              <div className={`kanban-col-header ${col.className}`}>
                <span>{col.label}</span>
                <span className="kanban-badge">{colItems.length}</span>
              </div>
              {colItems.map((row, idx) => {
                const { order, item, status } = row;
                const orderDate = new Date(order.createdAt || order.timestamp);
                const formattedTime = orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                
                const orderType = normalizeOrderType(order.orderType) || "N/A";
                let extrasList = [];
                if (item.addonQuantities) {
                  Object.entries(item.addonQuantities).forEach(([addonName, qty]) => {
                    if (qty > 0) {
                      const variantsText = formatAddonVariants(item.addonVariants?.[addonName]);
                      const customText = formatAddonCustomVariants(item.addonCustomVariantsDetails?.[addonName]);
                      let parts = [];
                      if (variantsText) parts.push(variantsText.replace(/[()]/g, ''));
                      if (customText) parts.push(customText);
                      let appendedText = parts.length > 0 ? `(${parts.join(", ")})` : "";
                      
                      extrasList.push({ type: "addon", name: `+ ${addonName} ${appendedText}`.trim() });
                      
                      if (item.addonToppings && item.addonToppings[addonName]) {
                        Object.values(item.addonToppings[addonName]).forEach(t => {
                          extrasList.push({ type: "addonTopping", name: `+ Topping: ${t.name} (for ${addonName})` });
                        });
                      }
                    }
                  });
                }
                
                if (item.comboQuantities) {
                  Object.entries(item.comboQuantities).forEach(([comboName, qty]) => {
                    if (qty > 0) {
                      const variantsText = formatComboVariants(item.comboVariants?.[comboName]);
                      const customText = formatComboCustomVariants(item.comboCustomVariantsDetails?.[comboName]);
                      let parts = [];
                      if (variantsText) parts.push(variantsText.replace(/[()]/g, ''));
                      if (customText) parts.push(customText);
                      let appendedText = parts.length > 0 ? `(${parts.join(", ")})` : "";
                      
                      extrasList.push({ type: "combo", name: `+ ${comboName} ${appendedText}`.trim() });
                      
                      if (item.comboToppings && item.comboToppings[comboName]) {
                        Object.values(item.comboToppings[comboName]).forEach(t => {
                          extrasList.push({ type: "comboTopping", name: `+ Topping: ${t.name} (for ${comboName})` });
                        });
                      }
                    }
                  });
                }
                
                if (item.customVariantsDetails) {
                  Object.keys(item.customVariantsDetails).forEach(vName => {
                    extrasList.push({ type: "variant", name: `+ ${vName}` });
                  });
                }
                
                return (
                  <div key={`${order.orderId}-${item.id}-${idx}`} className="kanban-card">
                    <div className="kanban-card-top">
                      <img 
                        src={item.image || "/static/images/default-item.jpg"} 
                        alt="item" 
                        className="kanban-item-img"
                        onError={(e) => { e.target.onerror = null; e.target.src = "/static/images/default-item.jpg"; }}
                      />
                      <div className="kanban-item-info">
                        <div className="kanban-item-title">
                          {item.name || item.item_name}
                          {(() => {
                            let parentheses = [];
                            if (item.selectedSize) parentheses.push(`Size: ${item.selectedSize}`);
                            
                            let icePref = item.icePreference || item.ice_preference || item.cold?.ice_preference || item.variants?.cold?.ice_preference;
                            if (icePref && icePref !== 'without_ice') {
                              let iceStr = icePref === 'with_ice' ? 'With Ice' : 
                                           icePref === 'less_ice' ? 'Less Ice' : icePref;
                              parentheses.push(`Ice: ${iceStr}`);
                            }
                            
                            let isSpicy = item.isSpicy || item.is_spicy || item.spicy?.is_spicy || item.variants?.spicy?.is_spicy;
                            if (isSpicy) parentheses.push(`Spicy`);
                            
                            return parentheses.length > 0 ? ` (${parentheses.join(', ')})` : '';
                          })()}
                        </div>
                        <div className="kanban-item-qty">×{item.quantity}</div>
                        <div className="kanban-pills-row">
                          <span className={`kanban-type-pill ${orderType.toLowerCase()}`}>{orderType}</span>
                          <span className="kanban-category">{item.category || "General"}</span>
                        </div>
                        <div className="kanban-order-no">{formatTokenNo(order.orderNo || "N/A")}</div>
                      </div>
                    </div>
                    
                    <div className="kanban-card-body">
                      {extrasList.length > 0 && (
                        <div className="kanban-addons" style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                          {extrasList.map((extra, i) => (
                            <div 
                              key={i} 
                              style={{ 
                                color: "#1e293b", 
                                fontSize: "12px", 
                                fontWeight: extra.type.includes("Topping") ? "500" : "600", 
                                paddingLeft: extra.type.includes("Topping") ? "10px" : "0px",
                                textAlign: "left"
                              }}
                            >
                              {extra.name}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {Object.entries(item.kitchenNotes || {}).map(([key, note]) => (
                         note?.trim() ? <div key={key} className="kanban-notes">{key}: {note.trim()}</div> : null
                      ))}
                    </div>
                    
                    <div className="kanban-card-footer">
                      <div className="kanban-time">
                        <FaClock style={{ fontSize: "10px" }} /> {formattedTime}
                      </div>
                      
                      {status === "Pending" && (
                        <button className="kanban-advance-btn" onClick={() => handleMarkPrepared(order.orderId, item.id, selectedKitchen || item.kitchen || (item.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen")}>
                          Prepare
                        </button>
                      )}
                      {status === "Preparing" && (
                        <button className="kanban-advance-btn" onClick={() => handleMarkPrepared(order.orderId, item.id, selectedKitchen || item.kitchen || (item.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen")}>
                          Prepare
                        </button>
                      )}
                      {status === "Prepared" && (
                        <button className="kanban-advance-btn" onClick={() => handlePickUp(order.orderId, item.id)}>
                          Prepared
                        </button>
                      )}
                      {(status === "Picked Up" || status === "PickedUp") && (
                        <button className="kanban-advance-btn done" disabled>
                          Pickedup
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="kitchen-container main-customer-page">
        {loading && (
          <div style={{ textAlign: "center", fontSize: "18px", padding: "10px" }}>Loading...</div>
        )}
        {errorMessage && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "10px",
              margin: "16px 40px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            {errorMessage}
            <button
              className="kitchen-btn-danger kitchen-btn-sm"
              style={{ marginLeft: "10px" }}
              onClick={() => {
                setErrorMessage("");
                fetchPreparedItems();
              }}
            >
              Retry
            </button>
          </div>
        )}

        <div className="customer-header-section">
          <div className="kitchen-title-block">
            <span className="k-subtitle">KITCHEN</span>
            <h1>Kitchen Services</h1>
            <p>Live view of incoming orders across all channels.</p>
          </div>
          <div className="kitchen-header-actions">
            <button className="kitchen-btn-primary-header" onClick={() => setShowStatusPopup(true)}>
              <FaClock style={{ marginRight: "6px" }} /> Last 1 hour
            </button>
            <button className="kitchen-btn-outline" onClick={() => setShowAllStatusPopup(true)}>
              All Status
            </button>
            <div style={{ position: "relative" }}>
              <button className="kitchen-btn-outline" onClick={() => setShowColumnManager(!showColumnManager)}>
                <FaSlidersH style={{ marginRight: "6px" }} /> Manage Columns
              </button>
              {showColumnManager && (
                <div style={{
                  position: "absolute", top: "100%", right: 0, marginTop: "8px",
                  backgroundColor: "white", border: "1px solid #ccc", borderRadius: "8px",
                  padding: "12px", zIndex: 1000, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  minWidth: "220px", display: "flex", flexDirection: "column", gap: "10px",
                  textAlign: "left"
                }}>
                  <div style={{ fontWeight: "bold", fontSize: "14px", borderBottom: "1px solid #eee", paddingBottom: "8px", marginBottom: "4px", color: "#333" }}>
                    Manage Columns
                  </div>
                  <div style={{ maxHeight: "300px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px", paddingRight: "4px" }}>
                    {Object.keys(tempVisibleColumns).map((col) => (
                      <label key={col} style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "14px", color: "#333", margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={tempVisibleColumns[col]}
                          onChange={() => toggleColumn(col)}
                          style={{ marginRight: "10px", width: "16px", height: "16px", cursor: "pointer" }}
                        />
                        {col}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px", borderTop: "1px solid #eee", paddingTop: "12px" }}>
                    <button
                      className="kitchen-btn-sm"
                      style={{ backgroundColor: "#6c757d", color: "white", padding: "6px 12px", borderRadius: "4px", border: "none", cursor: "pointer" }}
                      onClick={() => setShowColumnManager(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="kitchen-btn-sm"
                      style={{ backgroundColor: "#28a745", color: "white", padding: "6px 12px", borderRadius: "4px", border: "none", cursor: "pointer", fontWeight: "bold" }}
                      onClick={handleSaveColumns}
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="kitchen-metrics-container">
          <div className="k-metric-card pending">
            <div className="k-metric-header"><div className="k-dot"></div>PREPARE</div>
            <div className="k-metric-body">
              <span className="k-metric-number">{metrics.pending}</span>
              <span className="k-metric-label">Items</span>
            </div>
          </div>
          <div className="k-metric-card prepared">
            <div className="k-metric-header"><div className="k-dot"></div>PREPARED</div>
            <div className="k-metric-body">
              <span className="k-metric-number">{metrics.prepared}</span>
              <span className="k-metric-label">Ready</span>
            </div>
          </div>
          <div className="k-metric-card pickedup">
            <div className="k-metric-header"><div className="k-dot"></div>PICKEDUP</div>
            <div className="k-metric-value">
              <span className="k-metric-number">{metrics.pickedUp}</span>
              <span className="k-metric-label">orders</span>
            </div>
          </div>
        </div>

        <div className="kitchen-tabs-container">
          <div className="kitchen-tabs">
            {kitchens.map(kitchen => (
              <button 
                key={kitchen} 
                className={`k-tab ${selectedKitchen === kitchen ? 'active' : ''}`}
                onClick={() => setSelectedKitchen(kitchen)}
              >
                {kitchen}
              </button>
            ))}
          </div>
        </div>

        <div className="kitchen-main-content">
          <div className="kitchen-table-card">
            <div className="k-table-toolbar">
              <div className="k-toolbar-left">
                <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "4px" }}>
                  <h2 style={{ margin: 0 }}>Current Orders — Live Kitchen</h2>
                  <div className="k-view-toggle">
                    <button className={`k-view-btn ${activeDesign === "design1" ? "active" : ""}`} onClick={() => setActiveDesign("design1")}>Table View</button>
                    <button className={`k-view-btn ${activeDesign === "design2" ? "active" : ""}`} onClick={() => setActiveDesign("design2")}>Board View</button>
                  </div>
                </div>
                <p style={{ margin: 0 }}>{searchedItems.length} of {flattenedItems.length} orders shown</p>
              </div>
              <div className="k-toolbar-right">
                <div className="k-search-box">
                  <FaSearch className="k-search-icon" />
                  <input type="text" placeholder="Search item, order no, customer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>

                <button className="k-bulk-btn" disabled={selectedItems.length === 0} onClick={handleBulkMarkPrepared}>
                  Mark Prepared
                </button>
                <button className="k-bulk-btn" disabled={selectedItems.length === 0} onClick={handleBulkMarkPickedUp}>
                  Mark Picked Up
                </button>
              </div>
            </div>

            {activeDesign === "design1" ? (
              <div className="k-table-wrapper">
              <table className="k-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}><input type="checkbox" className="k-checkbox" checked={searchedItems.length > 0 && selectedItems.length === searchedItems.length} onChange={handleSelectAll} /></th>
                    <th>DATE - TIME</th>
                    <th>CUSTOMER</th>
                    <th>ORDER NO</th>
                    <th>TYPE</th>
                    <th>TABLE</th>
                    <th>ITEM & ADDONS</th>
                    <th>COMBO</th>
                    <th>IMAGE</th>
                    <th>QTY</th>
                    <th>CATEGORY</th>
                    <th>STATUS</th>
                    <th>KITCHEN NOTE</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {searchedItems.map((row, idx) => {
                    const { order, item, status } = row;
                    const orderDate = new Date(order.createdAt || order.timestamp);
                    const formattedDate = orderDate.toLocaleDateString();
                    const formattedTime = orderDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const statusClass = getStatusClass(status);
                    
                    let customerName = order.customerName || order.customer || "N/A";
                    let initial = customerName !== "N/A" ? customerName.charAt(0).toUpperCase() : "N";
                    if (customerName.toLowerCase().includes('table')) {
                        customerName = "N/A";
                        initial = "N";
                    }
                    const orderType = normalizeOrderType(order.orderType) || "N/A";

                    // Extras processing (Addons, Combos, Variants)
                    let extrasList = [];
                    if (item.addonQuantities) {
                      Object.entries(item.addonQuantities).forEach(([addonName, qty]) => {
                        if (qty > 0) {
                          const variantsText = formatAddonVariants(item.addonVariants?.[addonName]);
                          const customText = formatAddonCustomVariants(item.addonCustomVariantsDetails?.[addonName]);
                          let parts = [];
                          if (variantsText) parts.push(variantsText.replace(/[()]/g, ''));
                          if (customText) parts.push(customText);
                          let appendedText = parts.length > 0 ? `(${parts.join(", ")})` : "";
                          
                          extrasList.push({ type: "addon", name: `+ ${addonName} ${appendedText}`.trim() });
                          
                          if (item.addonToppings && item.addonToppings[addonName]) {
                            Object.values(item.addonToppings[addonName]).forEach(t => {
                              extrasList.push({ type: "addonTopping", name: `+ Topping: ${t.name} (for ${addonName})` });
                            });
                          }
                        }
                      });
                    }
                    
                    if (item.comboQuantities) {
                      Object.entries(item.comboQuantities).forEach(([comboName, qty]) => {
                        if (qty > 0) {
                          const variantsText = formatComboVariants(item.comboVariants?.[comboName]);
                          const customText = formatComboCustomVariants(item.comboCustomVariantsDetails?.[comboName]);
                          let parts = [];
                          if (variantsText) parts.push(variantsText.replace(/[()]/g, ''));
                          if (customText) parts.push(customText);
                          let appendedText = parts.length > 0 ? `(${parts.join(", ")})` : "";
                          
                          extrasList.push({ type: "combo", name: `+ ${comboName} ${appendedText}`.trim() });
                          
                          if (item.comboToppings && item.comboToppings[comboName]) {
                            Object.values(item.comboToppings[comboName]).forEach(t => {
                              extrasList.push({ type: "comboTopping", name: `+ Topping: ${t.name} (for ${comboName})` });
                            });
                          }
                        }
                      });
                    }
                    
                    if (item.customVariantsDetails) {
                      Object.keys(item.customVariantsDetails).forEach(vName => {
                        extrasList.push({ type: "variant", name: `+ ${vName}` });
                      });
                    }

                    const rowId = `${order.orderId}|${item.id}`;
                    return (
                      <tr key={`${rowId}-${idx}`}>
                        <td><input type="checkbox" className="k-checkbox" checked={selectedItems.includes(rowId)} onChange={() => handleSelectItem(rowId)} /></td>
                        <td className="k-td-datetime">
                          <div>{formattedDate}</div>
                          <div className="time">{formattedTime}</div>
                        </td>
                        <td>
                          <div className="k-customer-cell">
                            <div className="k-avatar">{initial}</div>
                            <span>{customerName}</span>
                          </div>
                        </td>
                        <td style={{ maxWidth: "120px", wordBreak: "break-word" }}>
                          {formatTokenNo(order.orderNo || "N/A")}
                        </td>
                        <td>
                          <span className={`k-type-pill ${orderType.toLowerCase()}`}>{orderType}</span>
                        </td>
                        <td style={{ color: "#64748b", fontWeight: 600 }}>
                           {orderType === "DIN" ? (
                                (order.customerName || order.customer)?.toLowerCase().includes('table')
                                  ? (order.customerName || order.customer)
                                  : (order.tableNumber ? `Table ${order.tableNumber}${Array.isArray(order.chairsBooked) && order.chairsBooked.filter(c => c > 0).length > 0 ? ` (Chair ${order.chairsBooked.filter(c => c > 0).join(', ')})` : ''}` : "N/A")
                              ) : "—"}
                        </td>
                        <td>
                          <div className="k-item-cell">
                            <span className="k-item-name">
                              {item.name || item.item_name}
                              {(() => {
                                let parentheses = [];
                                if (item.selectedSize) parentheses.push(`Size: ${item.selectedSize}`);
                                
                                let icePref = item.icePreference || item.ice_preference || item.cold?.ice_preference || item.variants?.cold?.ice_preference;
                                if (icePref && icePref !== 'without_ice') {
                                  let iceStr = icePref === 'with_ice' ? 'With Ice' : 
                                               icePref === 'less_ice' ? 'Less Ice' : icePref;
                                  parentheses.push(`Ice: ${iceStr}`);
                                }
                                
                                let isSpicy = item.isSpicy || item.is_spicy || item.spicy?.is_spicy || item.variants?.spicy?.is_spicy;
                                if (isSpicy) parentheses.push(`Spicy`);
                                
                                return parentheses.length > 0 ? ` (${parentheses.join(', ')})` : '';
                              })()}
                            </span>
                            {extrasList.length > 0 && (
                              <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "6px" }}>
                                {extrasList.map((extra, i) => (
                                  <div 
                                    key={i} 
                                    style={{ 
                                      color: "#1e293b", 
                                      fontSize: "13px", 
                                      fontWeight: extra.type.includes("Topping") ? "500" : "600", 
                                      paddingLeft: extra.type.includes("Topping") ? "12px" : "0px",
                                      textAlign: "left"
                                    }}
                                  >
                                    {extra.name}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {item.is_combo_offer ? (
                            <div className="k-item-cell">
                              <span className="k-combo-title">{item.offer_description || "Combo"}</span>
                              <span className="k-combo-text">{item.filteredComboOfferSubItems?.map(s => s.name).join(' • ') || "—"}</span>
                            </div>
                          ) : (
                            <span className="k-combo-text">—</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                              {getAddonComboImages(item).slice(0, 1).map((image, i) => (
                                <img 
                                  key={i}
                                  src={image.src} 
                                  alt={image.label} 
                                  className="k-item-image"
                                  onError={(e) => { e.target.onerror = null; e.target.src = "/static/images/default-item.jpg"; }}
                                />
                              ))}
                          </div>
                        </td>
                        <td style={{ fontWeight: 700 }}>×{item.quantity}</td>
                        <td style={{ color: "#64748b" }}>{item.category || "—"}</td>
                        <td>
                          <span className={`k-status-pill ${statusClass}`}>
                            <div className="k-dot"></div>
                            {status === "PickedUp" ? "Picked Up" : status}
                          </span>
                        </td>
                        <td>
                          <span className="k-note">
                             {Object.entries(item.kitchenNotes || {}).map(([key, note]) => (
                                note?.trim() ? <div key={key}>{key}: {note.trim()}</div> : null
                             ))}
                             {(!item.kitchenNotes || Object.values(item.kitchenNotes).every(n => !n?.trim())) && "No notes"}
                          </span>
                        </td>
                        <td>
                          {status === "Pending" && (
                            <button className="k-action-btn start" onClick={() => handleMarkPrepared(order.orderId, item.id, selectedKitchen || item.kitchen || (item.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen")}>
                              Prepare
                            </button>
                          )}
                          {status === "Preparing" && (
                            <button className="k-action-btn start" onClick={() => handleMarkPrepared(order.orderId, item.id, selectedKitchen || item.kitchen || (item.requiredKitchens && item.requiredKitchens[0]) || "Continental Kitchen")}>
                              Prepare
                            </button>
                          )}
                          {status === "Prepared" && (
                            <button className="k-action-btn start" onClick={() => handlePickUp(order.orderId, item.id)}>
                              Prepared
                            </button>
                          )}
                          {(status === "Picked Up" || status === "PickedUp") && (
                            <button className="k-action-btn done" disabled>
                              Pickedup
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            ) : (
              renderKanbanBoard()
            )}
          </div>
        </div>
        {/* FIXED: Changed from type="month" to type="date" and simplified filter logic */}
        {showStatusPopup && (
          <div className="kitchen-modal-overlay">
            <div className="kitchen-modal-content" style={{ padding: 0, backgroundColor: "#F8F9FD", overflow: "hidden", maxWidth: "900px" }}>
              <div style={{ padding: "24px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E6EF", backgroundColor: "#ffffff" }}>
                 <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#1B1B29" }}>Last Hour Pickup Status</h3>
                 <button onClick={() => setShowStatusPopup(false)} style={{ background: '#F1F4F9', border: 'none', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }}>
                   <FaTimes />
                 </button>
              </div>
              <div style={{ padding: "24px 32px" }}>
                <input
                  type="date"
                  value={lastHourSearchDate}
                  onChange={(e) => setLastHourSearchDate(e.target.value)}
                  style={{ marginBottom: "20px", height: "44px", padding: "0 16px", borderRadius: "8px", border: "1px solid #E2E6EF", outline: "none", fontSize: "14px", color: "#1B1B29", fontWeight: "500", backgroundColor: "#ffffff", display: "block" }}
                />
                <div style={{
                  maxHeight: '60vh', overflowY: 'auto', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)',
                  width: '100%', backgroundColor: '#ffffff', border: 'none'
                }}>
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>Order No</th>
                        <th>Customer</th>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Category</th>
                        <th>Kitchen</th>
                        <th>Pickup Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getLastHourItems().map((entry, index) => {
                        const subItems = getFlattenedSubItems(entry);
                        return (
                          <tr
                            key={index}
                            style={getHighlightStyle(entry.pickupTime, lastHourSearchDate)}
                          >
                            <td style={{ fontWeight: "600", color: "#1B1B29", verticalAlign: "top", paddingTop: "20px" }}>{formatTokenNo(entry.orderNo || entry.tokenNo || entry.orderId || "N/A")}</td>
                            <td style={{ fontWeight: "500", color: "#4A4A5A", verticalAlign: "top", paddingTop: "20px" }}>{entry.customerName || "Unknown"}</td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.name}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.quantity}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.category}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.kitchen}</div>
                              ))}
                            </td>
                            <td style={{ fontWeight: "500", color: "#4A4A5A", verticalAlign: "top", paddingTop: "20px" }}>{new Date(entry.pickupTime).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* FIXED: Changed from type="month" to type="date" and simplified filter logic */}
        {showAllStatusPopup && (
          <div className="kitchen-modal-overlay">
            <div className="kitchen-modal-content" style={{ padding: 0, backgroundColor: "#F8F9FD", overflow: "hidden", maxWidth: "900px" }}>
              <div style={{ padding: "24px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #E2E6EF", backgroundColor: "#ffffff" }}>
                 <h3 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#1B1B29" }}>All Pickup Status</h3>
                 <button onClick={() => setShowAllStatusPopup(false)} style={{ background: '#F1F4F9', border: 'none', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', color: '#1B1B29', transition: 'all 0.2s' }}>
                   <FaTimes />
                 </button>
              </div>
              <div style={{ padding: "24px 32px" }}>
                <input
                  type="date"
                  value={allStatusSearchDate}
                  onChange={(e) => setAllStatusSearchDate(e.target.value)}
                  style={{ marginBottom: "20px", height: "44px", padding: "0 16px", borderRadius: "8px", border: "1px solid #E2E6EF", outline: "none", fontSize: "14px", color: "#1B1B29", fontWeight: "500", backgroundColor: "#ffffff", display: "block" }}
                />
                <div style={{
                  maxHeight: '60vh', overflowY: 'auto', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)',
                  width: '100%', backgroundColor: '#ffffff', border: 'none'
                }}>
                  <table className="premium-table">
                    <thead>
                      <tr>
                        <th>Order No</th>
                        <th>Customer</th>
                        <th>Item</th>
                        <th>Quantity</th>
                        <th>Category</th>
                        <th>Kitchen</th>
                        <th>Pickup Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getAllPreparedItems().map((entry, index) => {
                        const subItems = getFlattenedSubItems(entry);
                        return (
                          <tr
                            key={index}
                            style={getHighlightStyle(entry.pickupTime, allStatusSearchDate)}
                          >
                            <td style={{ fontWeight: "600", color: "#1B1B29", verticalAlign: "top", paddingTop: "20px" }}>{formatTokenNo(entry.orderNo || entry.tokenNo || entry.orderId || "N/A")}</td>
                            <td style={{ fontWeight: "500", color: "#4A4A5A", verticalAlign: "top", paddingTop: "20px" }}>{entry.customerName || "Unknown"}</td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.name}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.quantity}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.category}</div>
                              ))}
                            </td>
                            <td style={{ padding: 0 }}>
                              {subItems.map((subItem, subIndex) => (
                                <div key={subIndex} style={{ padding: "20px 24px", borderBottom: subIndex !== subItems.length - 1 ? "1px solid #F1F3F8" : "none", fontWeight: "500", color: "#4A4A5A" }}>{subItem.kitchen}</div>
                              ))}
                            </td>
                            <td style={{ fontWeight: "500", color: "#4A4A5A", verticalAlign: "top", paddingTop: "20px" }}>{new Date(entry.pickupTime).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default Kitchen;